/**
 * Terra Signals — shared Zod schema, enums, and formula-id helpers.
 *
 * Canonical row shape for the Terra Signals read model (Notion "Terra
 * Signals Table, Playhouse and Action View Migrations", 3d67e66eb01480f79
 * 4efd1379ba27f85). This is Phase 1 infrastructure: the schema, not yet a
 * live view — see `plans/2026-09-09-002-terra-signals-phase1-plays-slice-plan.md`
 * U1. `TerraSignalSchema` is the wire/fixture shape (24 fields, matches
 * `docs/flow/signals-scoping/terra-actions-sample-records.csv` column-for-
 * column). `TerraSignalRowSchema` is the view-row shape the `terra_signals`
 * Postgres view actually emits: `action_link` dropped (never a column, D4)
 * and TWO view-only derived columns added (`signal_instance_id`,
 * `signal_status_rank`), plus `snooze_until` surfaced from the
 * `terra_signal_state` override table (U2).
 *
 * `has_responded` (plan `plans/2026-09-10-001-plays-tab-filter-redesign-plan.md`
 * D5/D6) is a real CSV/view column sitting immediately after
 * `signal_status`: Salesforce's own per-campaign `HasResponded`, never
 * re-derived from a status label. It replaced the derived column the Plays
 * tab used to group by, which no longer exists — the three Plays scopes are
 * ordinary filter presets over `signal_status` + `has_responded` now.
 *
 * Types are `z.infer` only (`terra/no-parallel-schema-and-type`) — never
 * hand-declare a parallel interface here.
 */

import { z } from 'zod';
import { GONG_FLOW_END_STATUSES } from '../flow/rep-state';

/**
 * The 15 locked Signal Types, Notion order. `Gift` is dropped from Phase 1
 * (Spencer 2026-09-09, plan "Resolved During Planning": Sendoso is not set
 * up; adding Gift back later is one enum value and one arm). The Notion
 * schema still lists Gift as a 16th value and gets corrected post-review —
 * this is not a missing value here.
 */
export const SIGNAL_TYPES = [
  'Job Change',
  'News',
  'Website Visit',
  'New Hire',
  'Job Posting',
  'Touchpoint',
  'Account Health',
  'Contract',
  'Consumption',
  'Social Engagement',
  'Campaign',
  'Gong Flow',
  'Message',
  'Open Loop',
  'Warm Intro',
] as const;
export const SignalTypeSchema = z.enum(SIGNAL_TYPES);
export type SignalType = z.infer<typeof SignalTypeSchema>;

/**
 * The 6 Action States. `Draft` and `Open` are dead ends ruled out (D13) —
 * neither is a valid state.
 */
export const ACTION_STATES = ['Insight', 'To do', 'Snoozed', 'Completed', 'Archived', 'Expired'] as const;
export const ActionStateSchema = z.enum(ACTION_STATES);
export type ActionState = z.infer<typeof ActionStateSchema>;

/**
 * The 8 Source Systems. `Gong` has no rows in the Phase 1 fixture (the
 * existing Terra-queue `gong_flow` arm produces `Terra queue` rows; a
 * dedicated `Gong` source-system adapter for contacts enrolled outside
 * Terra is deferred — see plan D9 / Deferred section).
 */
export const SOURCE_SYSTEMS = [
  'Salesforce Events and Signals',
  'Salesforce Signal Action',
  'Salesforce Campaign Member',
  'Salesforce Warm Intro',
  'Terra artifact',
  'Terra queue',
  'Keystone Open Loops',
  'Gong',
] as const;
export const SourceSystemSchema = z.enum(SOURCE_SYSTEMS);
export type SourceSystem = z.infer<typeof SourceSystemSchema>;

/**
 * `Suggested` — a Campaign row Terra minted from a suggestion or an unsent
 * queue item (arm tiers 3 and 4). Not a Salesforce status: Salesforce has
 * no row for these people yet.
 */
export const SUGGESTED_STATUS = 'Suggested';

/**
 * `HiL/Awaiting` — "human in the loop": Terra fired a send (arm tier 2) but
 * Salesforce has no mirrored campaign member for it. Flagged immediately,
 * with no grace window, because the normal path shadows tier 2 with a tier-1
 * member row as soon as the mirror catches up; a row still reading
 * `HiL/Awaiting` is a sync failure that needs a human.
 */
export const HIL_AWAITING_STATUS = 'HiL/Awaiting';

/**
 * The separator every arm joins a `signal_name`'s parts with — the first
 * argument of each arm's `concat_ws(...)`. ONE constant, because both arms
 * interpolate it (`arms/campaign.ts`, `arms/terra-queue.ts`) and
 * `src/shared/flow/signal-narrowing.ts` derives its narrowing-guard
 * invariant from it: a `signal_name contains ' · '` filter matches every
 * constructed row, so it narrows nothing.
 *
 * Changing this changes the emitted arm SQL (pinned byte-for-byte by
 * `__tests__/arms.test.ts` and the view-drift test) and the text every
 * existing snapshot row already carries — it is not a cosmetic knob.
 */
export const SIGNAL_NAME_SEPARATOR = ' · ';

/**
 * The one Signal Type the Campaign arm emits — interpolated into the arm SQL
 * (both the `signal_type` column and the first `concat_ws` argument of
 * `signal_name`, `arms/campaign.ts`) and declared on `campaignArm` there, so
 * the emitted text and the arm's metadata cannot disagree.
 *
 * Lives here (U4, plan `plans/2026-09-24-005-plays-tab-one-pr-plan.md`),
 * not in `arms/campaign.ts`, so `CAMPAIGN_SIGNAL_NAME_INVARIANT_PREFIX`
 * immediately below — needed by the zero-dependency
 * `src/shared/flow/signal-narrowing.ts` — has no dependency on a
 * Prisma-importing arm module. `arms/campaign.ts` re-exports both constants
 * from here so its own call sites are unchanged.
 */
export const CAMPAIGN_SIGNAL_TYPE: SignalType = 'Campaign';

/**
 * Text that EVERY Campaign-arm row's `signal_name` is guaranteed to start
 * with.
 *
 * The guarantee, derived from the arm SQL in `arms/campaign.ts` and not from
 * a sample of the data: `signal_name` is `concat_ws(SEPARATOR, 'Campaign',
 * c.type, c.name, t.status)`. The first argument is a LITERAL (never NULL,
 * so `concat_ws` never skips it) and `c.name` is non-null on
 * `launcher_sfdc_campaigns`, so at least one more part always follows it and
 * the separator is therefore always emitted after it. Hence every row begins
 * with `'Campaign · '`.
 *
 * CONSUMER: `src/shared/flow/signal-narrowing.ts`. Its Signal narrowing
 * guard (`conditionValueNarrowsSignals`) rejects a `signal_name contains
 * <value>` filter whose value is a case-insensitive substring of this
 * prefix — such a value matches the entire 246k-row snapshot by
 * construction, so it bounds the Signal LATERAL by nothing (the measured B2
 * 25,051ms shape). That is a derivation about the SQL above, NOT a minimum-
 * length floor on the value (a floor was considered and rejected: 'Z' is
 * selective, 'ampaig' is not).
 */
export const CAMPAIGN_SIGNAL_NAME_INVARIANT_PREFIX = `${CAMPAIGN_SIGNAL_TYPE}${SIGNAL_NAME_SEPARATOR}`;

/**
 * The generic fallback identity for `action_completed_by`/
 * `action_completed_by_name` when a campaign member was never sent via Terra
 * (a member added directly in Salesforce) — `arms/campaign.ts` tier 1's
 * `COALESCE(ia_sender.user_email, SALESFORCE_SYNC_ACTOR)`. Exported as one
 * named constant, imported by both the arm (backend) and
 * `ContactFlowTable.tsx` (frontend, which renders it as "Unknown" rather
 * than the raw sentinel), so the two can never drift on the literal string.
 */
export const SALESFORCE_SYNC_ACTOR = 'salesforce-sync';

/**
 * Canonical Campaign status order (13 labels): the two Terra-minted
 * statuses above, then the 11 mirrored Salesforce Campaign Member labels.
 * Drives `signal_status_rank` for Campaign rows when a per-campaign
 * `sort_order` is not mirrored, and is the source for the SQL
 * `array_position(ARRAY[...])` expression in the Campaign arm — that array
 * literal is generated from this constant so the two cannot disagree.
 *
 * Order is rep-facing priority, not lifecycle: `Suggested` (nothing has
 * happened yet, the rep can act) sorts above `HiL/Awaiting` (needs a human
 * to unstick) above the mirrored statuses.
 */
export const CAMPAIGN_STATUS_ORDER = [
  SUGGESTED_STATUS,
  HIL_AWAITING_STATUS,
  'Invited',
  'Sent',
  'Registered',
  'Responded',
  'Pending approval',
  'Approved',
  'Declined',
  'Attended',
  'No-show',
  'Viewed replay',
  'Met with',
] as const;

/**
 * The closed Signal Status vocabulary (D10). `Pending` and `Removed from
 * flow` are not emitted by this arm — they require the `gong_flow_probe`
 * join U4 adds — but the array is the FULL closed vocabulary now so
 * `signal_status_rank` never has to be regenerated when U4 lands.
 *
 * Lives HERE rather than in `src/services/terra-signals/arms/signal-action.ts`
 * (R19, review round 1, fix plan D-M) because it is now read from two sides:
 * the arm derives its `array_position(ARRAY[...])` literal and its
 * flow-status normalization map from it, and the shared Flow filter catalog
 * needs it to offer the Signals tab's real "Flow status" values instead of
 * inheriting the Campaign vocabulary. `src/shared/` is the only place both
 * can import from — the arm already imports `SIGNAL_NAME_SEPARATOR` and
 * `SignalType` from this file, so this is the direction the dependency
 * already runs.
 */
export const SIGNAL_ACTION_STATUS_ORDER = [
  'Running',
  'Pending',
  'Paused',
  'Removed from flow',
  'Finished',
  // Gong end statuses — derived, never retyped (`rep-state.ts` is the source
  // of truth for the wire strings; U21).
  ...GONG_FLOW_END_STATUSES,
  'No flow',
  // Gong sent a status we do not recognise — sorts last; never treated as live.
  'Unknown',
] as const;

/**
 * Wire form for a date/timestamp field: a string, `YYYY-MM-DD` or full ISO,
 * that `Date.parse` accepts. Used for `event_date`, `due_date`,
 * `action_completed_at`, and `snooze_until`.
 */
export const IsoDateStringSchema = z.string().refine((value) => !Number.isNaN(Date.parse(value)), {
  message: 'Expected an ISO-parseable date string',
});
export type IsoDateString = z.infer<typeof IsoDateStringSchema>;

/**
 * Base object shape, kept unrefined so `TerraSignalRowSchema` below can
 * `.omit()`/`.extend()` it directly. `TerraSignalSchema` (below) is this
 * object plus the contact-or-account refinement — refinements attach
 * without losing `.omit`/`.extend` in Zod 4, but keeping the pre-refine
 * object explicit makes that dependency visible rather than incidental.
 */
const TerraSignalObjectSchema = z.object({
  signal_id: z.string().min(1),
  signal_type: SignalTypeSchema,
  signal_subtype: z.string().nullable(),
  signal_name: z.string().min(1),
  signal_status: z.string().nullable(),
  // Salesforce's own per-campaign `HasResponded` on the mirrored member row
  // (D6) — a campaign configured so `Registered` does not count as a
  // response reads that way here too. NULL on every non-Campaign row.
  has_responded: z.boolean().nullable(),
  signal_tactic: z.string().nullable(),
  // R7 (2026-09-24) — six Signal Action parity columns, carried by every
  // arm so the client-side Ranker (D1) can match against the raw
  // (type, subtype, playType) triple instead of the normalized
  // `signal_type`/`signal_subtype`. Real for the Signal Action arm
  // (`arms/signal-action.ts`); typed NULL on the Campaign and Terra-queue
  // arms, which have no Signal Action row to read these off of. Optional
  // so the 25-column CSV fixture (which predates these) still parses, same
  // reasoning as `assignee_id`; nullable because SFDC leaves them blank on
  // plenty of real rows.
  signal_source_type: z.string().nullable().optional(),
  signal_source_subtype: z.string().nullable().optional(),
  play_type: z.string().nullable().optional(),
  step_number: z.number().int().nullable().optional(),
  priority_stamp: z.number().nullable().optional(),
  days_open: z.number().nullable().optional(),
  action_state: ActionStateSchema,
  action_reason: z.string().nullable(),
  action_completed_at: IsoDateStringSchema.nullable(),
  action_completed_by: z.string().nullable(),
  // "Actioned By" (2026-09-22 plan) — the SFDC-user id/name pair for whoever
  // completed the action, mirroring the assignee_id/assignee_email triple
  // from U1 above exactly: `action_completed_by` (email) stays a plain text
  // field, and these two are the `{me}`-resolvable name + its indexed id
  // companion. Optional (not part of the 25-column CSV fixture, same
  // reasoning as assignee_id/assignee_email) and nullable (a row whose
  // completer has no salesforce_user mirror row, or was never completed,
  // has neither).
  action_completed_by_id: z.string().nullable().optional(),
  action_completed_by_name: z.string().nullable().optional(),
  // Never a stored column (D13) — optional so view rows (which carry no
  // such key) still parse; nullable so the CSV fixture's empty cells parse.
  action_link: z.string().nullable().optional(),
  account_id: z.string().nullable(),
  account_name: z.string().nullable(),
  contact_id: z.string().nullable(),
  contact_name: z.string().nullable(),
  assignee: z.string().nullable(),
  assignee_role: z.string().nullable(),
  assignee_manager: z.string().nullable(),
  // U1 — not a CSV column (the 25-column fixture predates it, D4), so
  // optional so that fixture row still parses; nullable because a row with
  // no resolved account owner (or owner with no salesforce_user mirror row)
  // has neither.
  assignee_id: z.string().nullable().optional(),
  assignee_email: z.string().nullable().optional(),
  event_date: IsoDateStringSchema.nullable(),
  due_date: IsoDateStringSchema.nullable(),
  source_system: SourceSystemSchema,
  source_record_id: z.string().min(1),
  source_link: z.string().nullable(),
  // A9 (`plans/2026-09-10-001-plays-tab-filter-redesign-plan.md`) — the
  // Clay-pushed suggestion's own classification of the person, straight off
  // `launcher_event_invitees.invitee_type` (free text: `Prospect`,
  // `Customer`, …). Campaign-arm only; NULL on every other arm, and NULL on
  // a Campaign row with no matching invitee record.
  invitee_type: z.string().nullable(),
});

/**
 * Canonical Terra Signal row — exactly the 25
 * `terra-actions-sample-records.csv` columns, in CSV order. A row must
 * carry at least one of `contact_id` / `account_id` (a Signal is always
 * about a person or a company).
 */
export const TerraSignalSchema = TerraSignalObjectSchema.refine((row) => row.contact_id != null || row.account_id != null, {
  message: 'A Terra Signal must have a contact_id or an account_id',
});
export type TerraSignal = z.infer<typeof TerraSignalSchema>;

/**
 * The `terra_signals` Postgres view's row shape (D4): `action_link` dropped
 * (never a column) and two view-only derived columns added, plus
 * `snooze_until` from the `terra_signal_state` override table.
 */
export const TerraSignalRowSchema = TerraSignalObjectSchema.omit({ action_link: true }).extend({
  signal_instance_id: z.string().nullable(),
  signal_status_rank: z.number().int().nullable(),
  snooze_until: IsoDateStringSchema.nullable(),
});
export type TerraSignalRow = z.infer<typeof TerraSignalRowSchema>;

/**
 * The Phase 1 signal-id "kinds" — the `<kind>:...` prefix on `signal_id`.
 * `campaign` and `gong_flow` are formula ids (D10); `message` is
 * record-based but named here because the Terra-queue arm (U4/D9) mints it.
 * `signal_action` is record-based (`recordSignalId('signal_action', sa.id)`
 * shape) — the `signal_action` arm (U2) mints it as `'signal_action:' ||
 * sa.id` directly in SQL.
 */
export const SIGNAL_ID_KIND = {
  campaign: 'campaign',
  gong_flow: 'gong_flow',
  message: 'message',
  signal_action: 'signal_action',
} as const;

/**
 * Formula id for a Campaign row: `campaign:<campaignId>:<contactId>`.
 * Identity is `(campaign, contact)`, not the campaign-member record, so it
 * survives a re-send without orphaning `terra_signal_state` (D10).
 */
export function campaignSignalId(campaignId: string, contactId: string): string {
  return `${SIGNAL_ID_KIND.campaign}:${campaignId}:${contactId}`;
}

/**
 * Formula id for a Gong Flow queue row: `gong_flow:<flowId>:<contactId>`.
 */
export function gongFlowSignalId(flowId: string, contactId: string): string {
  return `${SIGNAL_ID_KIND.gong_flow}:${flowId}:${contactId}`;
}

/**
 * Record-based id for every non-formula signal: `<kind>:<recordId>`. `kind`
 * must be lowercase-and-underscore (`/^[a-z_]+$/`) — it is the SQL/parse
 * boundary between the id's "type" and its free-text record reference, and
 * a kind with spaces or mixed case would make `parseSignalId` ambiguous
 * against ids whose record id itself contains a colon (e.g. Open Loop's
 * `open_loop:<accountId>:<index>`).
 */
export function recordSignalId(kind: string, recordId: string): string {
  if (!/^[a-z_]+$/.test(kind)) {
    throw new Error(`recordSignalId: kind must match /^[a-z_]+$/, got "${kind}"`);
  }
  return `${kind}:${recordId}`;
}

/**
 * Splits a `signal_id` into its leading kind and the remaining `:`-joined
 * parts. `campaignSignalId(parts[0], parts[1])` round-trips a Campaign
 * row's id back to the original string.
 */
export function parseSignalId(id: string): { kind: string; parts: string[] } {
  const [kind, ...parts] = id.split(':');
  return { kind: kind ?? '', parts };
}

/**
 * The raw Salesforce record id for a `signal_action:<id>` signal id, or null
 * for any other kind (`campaign:…`, `gong_flow:…`) or a malformed id. Those
 * other kinds have no `Signal_Action__c` record.
 */
export function signalActionRecordId(signalId: string): string | null {
  const { kind, parts } = parseSignalId(signalId);
  if (kind !== SIGNAL_ID_KIND.signal_action) return null;
  if (parts.length !== 1 || parts[0] === '') return null;
  return parts[0];
}

/**
 * Request body for the Archive Signal Action action (U5, P6). Salesforce's
 * `Require_Archive_Reason_On_Close` validation rule fires on every API write
 * path, so `reason` and `feedback` are BOTH mandatory here — the route
 * cannot send a status-only PATCH. Archive is not reversible (no Reopen),
 * so there is no analogous "unarchive" body.
 */
export const ArchiveSignalActionBodySchema = z.object({
  reason: z.string().min(1),
  feedback: z.string().min(1),
});
export type ArchiveSignalActionBody = z.infer<typeof ArchiveSignalActionBodySchema>;

/**
 * Request body for the Snooze Signal Action action (U5, P7). `snoozeUntil`
 * is an ISO **instant** (the moment the signal un-snoozes), not a calendar
 * day: the client sends the rep's local midnight, because a bare
 * `YYYY-MM-DD` is read as UTC midnight and is already in the past for a
 * negative-offset rep. The handler refuses any instant at or before `now()`
 * with code `'past_date'`.
 *
 * It is optional at the schema level but conditionally required by
 * `snoozeSignalAction`'s own validation (400-shaped) when the signal's Gong
 * Engage Flow signature is incomplete — that check needs the mirror data,
 * not just the body shape, so it is not expressible as a Zod refinement
 * here.
 */
export const SnoozeSignalActionBodySchema = z.object({
  snoozeUntil: IsoDateStringSchema.optional(),
});
export type SnoozeSignalActionBody = z.infer<typeof SnoozeSignalActionBodySchema>;

/**
 * `GET /api/flow/signals/archive-reasons` success body (U6, R8/R14). Wraps
 * the `describeSalesforcePicklist` response (`DescribePicklistResponse`,
 * `src/services/salesforce/reads/describe-picklist.ts`) down to just the `values`
 * shape the client needs, already filtered to manual (non-Gong-passthrough,
 * active) reasons by `filterManualArchiveReasons`
 * (`src/services/terra-signals/archive-reasons.ts`).
 */
export const ArchiveReasonsResponseSchema = z.object({
  values: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
      active: z.boolean(),
    }),
  ),
});
export type ArchiveReasonsResponse = z.infer<typeof ArchiveReasonsResponseSchema>;

/**
 * `GET /api/flow/signals/:signalId/open` query (U6, R13/R16) — a copy of
 * Action View's `OpenGongQuerySchema` (`mini-apps/action-view/types.ts`).
 * Trimmed non-empty optional strings, same as the mini-app: an omitted id is
 * fine (falls back to the Gong `/todos` base), a blank one is not.
 *
 * `source` is the attribution marker (plan 2026-10-13-001 U2): the Slack
 * notifier's URL builder appends `source=slack_notification`, and only that
 * exact value makes the route emit "Slack Notification Clicked"
 * (`button: 'verb'`). Any other or absent value stays silent.
 */
export const SignalOpenQuerySchema = z.object({
  contactId: z.string().trim().min(1).optional(),
  accountId: z.string().trim().min(1).optional(),
  source: z.string().trim().min(1).optional(),
  /**
   * The row's step type, for the `task_opened_in_gong` analytics event only.
   * Parsed independently (`.catch`): a malformed value drops to `undefined`
   * instead of failing the whole query, which would lose the contact/account
   * ids and Slack attribution this redirect exists to carry.
   */
  stepType: z.string().trim().min(1).max(64).optional().catch(undefined),
  /** The row's step number, same analytics-only, independently-parsed contract as `stepType`. */
  stepNumber: z
    .string()
    .regex(/^\d{1,4}$/)
    .transform(Number)
    .optional()
    .catch(undefined),
});
export type SignalOpenQuery = z.infer<typeof SignalOpenQuerySchema>;

/** Snooze success body (U6) — matches `res.status(200).json({ snoozed: true })` at flow-signals.ts:150-260. */
export const SnoozeResultSchema = z.object({ snoozed: z.literal(true) });
export type SnoozeResult = z.infer<typeof SnoozeResultSchema>;

/** Unsnooze success body (U6) — matches `res.status(200).json({ unsnoozed: result.unsnoozed })`. */
export const UnsnoozeResultSchema = z.object({ unsnoozed: z.boolean() });
export type UnsnoozeResult = z.infer<typeof UnsnoozeResultSchema>;

/** Archive success body (U6) — matches `res.status(200).json({ alreadyArchived: result.alreadyArchived })`. */
export const ArchiveResultSchema = z.object({ alreadyArchived: z.boolean() });
export type ArchiveResult = z.infer<typeof ArchiveResultSchema>;

/**
 * `POST /api/flow/signals/:signalId/dialer-event` body (gong reconcile U18):
 * a Trellus dialer hint that the rep completed the row's Gong task, or
 * removed the contact from the flow. A HINT, not a command — the server
 * re-checks the row through the reconcile shell (plan D8 for `flow_removed`).
 * `eventId` is Trellus's own event id (replay dedupe). Strict: an unknown key
 * (e.g. `is_test`) is a client bug, not something to ignore.
 */
export const DialerEventBodySchema = z
  .object({
    kind: z.enum(['task_completed', 'flow_removed']),
    eventId: z.string().min(1).max(128),
    gongTaskId: z.string().optional(),
    gongFlowInstanceId: z.string().optional(),
    /** The step the completion refers to (Gong's pinned task). A task_completed with no step, or a step that differs from the mirror, is a facts-only re-check, never a close. */
    gongStepNumber: z.number().int().positive().optional(),
  })
  .strict();
export type DialerEventBody = z.infer<typeof DialerEventBodySchema>;

/**
 * Dialer-event success body. `outcome` summarises the rep's rows on that
 * enrollment; `resurfaces` is the snooze's backstop date (`snoozed` only,
 * `null` when the snooze lifts on the next Gong step instead);
 * `affectedSignalIds` are the rows now out of the rep's queue (closed,
 * snoozed, or found already closed).
 */
export const DialerEventResultSchema = z.object({
  outcome: z.enum(['closed', 'snoozed', 'already_closed', 'already_snoozed', 'unchanged']),
  resurfaces: IsoDateStringSchema.nullable(),
  affectedSignalIds: z.array(z.string()),
});
export type DialerEventResult = z.infer<typeof DialerEventResultSchema>;

/**
 * Most loaded rows one refresh may name: one Signals list page
 * (`FLOW_PAGE_SIZE` = 500, `src/services/flow/query/keyset.ts`) plus the
 * keyset lookahead row. Pinned here rather than imported — `src/shared/**`
 * never imports `src/services/**`.
 */
export const REFRESH_SIGNALS_MAX_SIGNAL_IDS = 501;

/**
 * `POST /api/flow/signals/refresh` request body (QA batch 2 B4, plan D-D).
 * Empty on the mount path; the Refresh button sends the loaded rows'
 * `signal_id`s so their Gong enrollment is re-verified live alongside the
 * rep's own rows. Only `signal_action:` ids have a Gong flow — any other id
 * (or an unknown key) is a malformed request, not something to drop.
 */
export const RefreshSignalsBodySchema = z
  .object({
    signalIds: z
      .array(z.string().regex(/^signal_action:.+/))
      .max(REFRESH_SIGNALS_MAX_SIGNAL_IDS)
      .optional(),
  })
  .strict();
export type RefreshSignalsBody = z.infer<typeof RefreshSignalsBodySchema>;

/**
 * The ONE request budget for `POST /api/flow/signals/refresh`
 * (fix-plan-batch2 D-C). Two consumers derive from this number, so they can
 * never drift apart:
 *   - the client's `apiFetch` `timeoutMs` in
 *     `web/app-home/src/components/flow/signals/useRefreshSignals.ts`;
 *   - the server's interactive live-check deadline in
 *     `refreshSignalActionsForRep` (`src/services/terra-signals/signal-action-refresh.ts`):
 *     `readStartedAt + REFRESH_SIGNALS_CLIENT_TIMEOUT_MS - LIVE_CHECK_RESPONSE_RESERVE_MS`,
 *     so the response is written before the client gives up.
 */
export const REFRESH_SIGNALS_CLIENT_TIMEOUT_MS = 60_000;

/**
 * The live Gong check's summary on a successful refresh (B4, D-D step 5) —
 * for the server log, tests and QA; the client reads nothing from it.
 * Mirrors `GongLiveCheckResult` (`src/services/gong-flow-probe/live-check.ts`).
 * `checked` counts pairs whose verdict was written (fence-superseded pairs
 * are logged, not counted); `deadlineHit` is `true` when the interactive
 * deadline abandoned or skipped any batch (those count in `failedBatches`).
 */
export const RefreshSignalsGongCheckSchema = z.object({
  checked: z.number().int().nonnegative(),
  enrolled: z.number().int().nonnegative(),
  notEnrolled: z.number().int().nonnegative(),
  failedBatches: z.number().int().nonnegative(),
  deadlineHit: z.boolean(),
});

/**
 * `POST /api/flow/signals/refresh` response body (U7). Covers all three
 * shapes `createPostSignalsRefreshHandler` (`flow-signals.ts`) can emit:
 * 200 `{ refreshed: true, rows, gongCheck }` (success; `gongCheck` is `null`
 * when the live Gong check was skipped), 200 `{ refreshed: false,
 * throttled: true }` (already claimed within the 5-minute window), and 202
 * `{ refreshed: false }` (fail-soft — Middleman/parse error, or no
 * Salesforce owner email). `throttled`/`rows`/`gongCheck` are optional so the
 * 202 body (which carries none of them) still parses.
 */
export const RefreshSignalsResponseSchema = z.object({
  refreshed: z.boolean(),
  throttled: z.boolean().optional(),
  rows: z.number().optional(),
  gongCheck: RefreshSignalsGongCheckSchema.nullable().optional(),
});
export type RefreshSignalsResponse = z.infer<typeof RefreshSignalsResponseSchema>;
